import { CardProduct } from '@/molecules';

import {
  getPercentageDiscount,
  gtmPush,
  isVariable,
  toCurrency,
} from '@/lib/utils';
import { normalizeAlgoliaItem } from '@/lib/utils/gtm/normalizeAlgoliaItem';

import type { TypeProduct } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeCardProductAlgoliaProps } from './types';

/**
 * CardProductAlgolia
 */
export const CardProductAlgolia: FunctionComponent<
  TypeCardProductAlgoliaProps
> = ({
  hit,
  type = 'COMPLETE',
  sendEvent,
  itemListId,
  itemListName,
  itemIndex,
}: TypeCardProductAlgoliaProps) => {
  const isFalseVariable = (hit: any) =>
    isVariable(hit.type) && hit.variations.length === 1;
  const productID = isFalseVariable(hit) ? hit.variations[0].id : hit.id;
  const productType: TypeProduct = isFalseVariable(hit) ? 'SIMPLE' : hit.type;

  const onClick = () => {
    gtmPush({
      event: 'select_item',
      ecommerce: {
        items: [normalizeAlgoliaItem(hit, itemListId, itemListName, itemIndex)],
      },
    });
  };

  return (
    <CardProduct
      href={hit.path}
      type={productType}
      productID={productID}
      stock={hit.stock_status}
      hit={hit}
      sendEvent={sendEvent}
      {...(hit?.card?.box && hit?.card?.box?.active
        ? { box: hit?.card?.box }
        : {})}
      onClick={onClick}
    >
      <CardProduct.Image
        src={hit?.card?.image?.src ?? hit.featured_image?.src}
        alt={hit?.card?.image?.alt ?? hit.featured_image?.alt}
        {...(hit?.card?.backgroundColor
          ? { backgroundColor: hit?.card?.backgroundColor }
          : {})}
      />

      {hit.primary_category && (
        <CardProduct.Category
          href={hit.primary_category.path.replace('categoria-producto/', '')}
        >
          {hit.primary_category.name}
        </CardProduct.Category>
      )}

      {type === 'COMPLETE' && hit.on_sale && (
        <CardProduct.Badge
          style={hit?.card?.badge?.style}
          outline={hit?.card?.badge?.outline}
          roundedRight={!hit?.card?.life_style}
        >
          {`-${getPercentageDiscount(
            hit.regular_price
              ? hit.regular_price
              : hit.variations[0]?.regular_price,
            hit.sale_price ? hit.sale_price : hit.variations[0]?.sale_price,
          )}%`}
        </CardProduct.Badge>
      )}

      <CardProduct.Name>
        {hit?.card?.name ? hit.card.name : hit.name}
      </CardProduct.Name>

      {hit.seller && hit.seller.length > 0 && (
        <CardProduct.Seller>{hit.seller[0]}</CardProduct.Seller>
      )}

      {type === 'COMPLETE' && hit.on_sale && (
        <CardProduct.PriceRegular>
          {toCurrency(
            hit.regular_price
              ? hit.regular_price
              : hit.variations[0]?.regular_price,
          )}
        </CardProduct.PriceRegular>
      )}

      {type === 'COMPLETE' && (
        <CardProduct.PriceDiscounted>
          {toCurrency(hit.price ? hit.price : hit.variations[0]?.price)}
        </CardProduct.PriceDiscounted>
      )}
    </CardProduct>
  );
};

CardProductAlgolia.displayName = 'CardProductAlgolia';
export default CardProductAlgolia;
